import { Divider, Group, Stack, TagsInput, Text } from '@mantine/core';
import { useCompanyConfiguration } from 'init/use-company-configuration';
import { i18nKeys, useTranslation } from 'locales';
import { useLoadMail } from 'modules/Mails/hooks';
import { useSendEmailFormContext } from 'modules/Mails/pages/NewMail/hooks';
import { getLastMail } from 'modules/Mails/utils/thread';
import { type ComponentPropsWithoutRef, useEffect, useState } from 'react';
import { useParams } from 'react-router';

export const MailComposerRecipients = () => {
  const form = useSendEmailFormContext();
  const { t } = useTranslation();
  const company = useCompanyConfiguration();
  const { id } = useParams<{ id: string }>();

  const { thread } = useLoadMail(id);

  const [isCcVisible, setIsCcVisible] = useState(false);
  const [isBccVisible, setIsBccVisible] = useState(false);

  // biome-ignore lint/correctness/useExhaustiveDependencies: intentionally runs once on mount to pre-fill reply form
  useEffect(() => {
    const lastMail = getLastMail(thread);
    if (!lastMail) return;
    // Skip auto-fill if subject is already set (e.g. initialized from a saved draft)
    if (form.values.subject !== '') return;
    const isLastMailSentFromRecovr =
      lastMail.fromMail === `${company.default_from}@${company.custom_domain}`;
    const ccMail = [lastMail.cc ?? []].flat();
    const bccMail = [lastMail.bcc ?? []].flat();
    form.setValues({
      toMail: isLastMailSentFromRecovr ? lastMail.toMail : [lastMail.fromMail],
      ccMail,
      bccMail,
      subject: `RE: ${lastMail.subject ?? ''}`,
    });
    setIsCcVisible(ccMail.length > 0);
    setIsBccVisible(bccMail.length > 0);
  }, []);

  return (
    <Stack p="space3" gap={0}>
      <Group gap={0} align="center" wrap="nowrap">
        <Text size="xs" c="dimmed" w={28} style={{ flexShrink: 0 }}>
          {t(i18nKeys.FORM.TO)}
        </Text>
        <TagsInput
          variant="unstyled"
          size="sm"
          style={{ flex: 1 }}
          placeholder={t(i18nKeys.EXTERNAL_MAIL.RECIPIENTS_PLACEHOLDER)}
          {...form.getInputProps('toMail')}
        />
        <Group gap="space2" pr="space2" style={{ flexShrink: 0 }}>
          {!isCcVisible && (
            <Text
              size="xs"
              c="dimmed"
              style={{ cursor: 'pointer' }}
              onClick={() => setIsCcVisible(true)}
            >
              {t(i18nKeys.EXTERNAL_MAIL.CC)}
            </Text>
          )}
          {!isBccVisible && (
            <Text
              size="xs"
              c="dimmed"
              style={{ cursor: 'pointer' }}
              onClick={() => setIsBccVisible(true)}
            >
              {t(i18nKeys.EXTERNAL_MAIL.BCC)}
            </Text>
          )}
        </Group>
      </Group>
      {isCcVisible && (
        <RecipientRow label={t(i18nKeys.EXTERNAL_MAIL.CC)} {...form.getInputProps('ccMail')} />
      )}
      {isBccVisible && (
        <RecipientRow label={t(i18nKeys.EXTERNAL_MAIL.BCC)} {...form.getInputProps('bccMail')} />
      )}
    </Stack>
  );
};

type RecipientRowProps = { label: string } & Pick<
  ComponentPropsWithoutRef<typeof TagsInput>,
  'value' | 'onChange' | 'error'
>;

const RecipientRow = ({ label, ...inputProps }: RecipientRowProps) => (
  <>
    <Divider />
    <Group gap={0} align="center" wrap="nowrap">
      <Text size="xs" c="dimmed" w={28} style={{ flexShrink: 0 }}>
        {label}
      </Text>
      <TagsInput
        variant="unstyled"
        size="sm"
        style={{ flex: 1 }}
        placeholder={label}
        {...inputProps}
      />
    </Group>
  </>
);
